/**
 * State for the Admin Console (DEV-3767).
 *
 * Owns the scoped user list and the write operations the console exposes. All
 * writes re-read the user from the response cloud-core sends back — the server
 * is the authority on what the caller may still see after a change — so the
 * console never guesses at the outcome of a write it did not make.
 */
import { useCallback, useEffect, useState } from "react";

import {
  AdminUser,
  AssignableRole,
  InviteUserPayload,
  assignAdminUserCompany,
  getAdminUsers,
  inviteAdminUser,
  revokeAdminUserCompany,
  setAdminUserActive,
  setAdminUserRole,
} from "Utils/adminUsersAPI";
import { showNetworkErrorToast } from "Utils/api";

export interface AdminUsersState {
  users: AdminUser[];
  isLoading: boolean;
  isLoadingError: boolean;
  reload: () => Promise<void>;
  invite: (payload: InviteUserPayload) => Promise<boolean>;
  changeRole: (
    userId: string,
    companyId: string,
    role: AssignableRole
  ) => Promise<boolean>;
  setActive: (userId: string, isActive: boolean) => Promise<boolean>;
  assignCompany: (
    userId: string,
    companyId: string,
    role?: AssignableRole
  ) => Promise<boolean>;
  revokeCompany: (userId: string, companyId: string) => Promise<boolean>;
}

const replaceUser = (users: AdminUser[], updated: AdminUser): AdminUser[] =>
  users.map((user) => (user.id === updated.id ? updated : user));

export const useAdminUsers = (): AdminUsersState => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setIsLoadingError(false);
    try {
      setUsers(await getAdminUsers());
    } catch (error) {
      setIsLoadingError(true);
      showNetworkErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const invite = useCallback(async (payload: InviteUserPayload) => {
    try {
      const created = await inviteAdminUser(payload);
      setUsers((current) => [
        ...current.filter((user) => user.id !== created.id),
        created,
      ]);
      return true;
    } catch (error) {
      showNetworkErrorToast(error);
      return false;
    }
  }, []);

  const changeRole = useCallback(
    async (userId: string, companyId: string, role: AssignableRole) => {
      try {
        const updated = await setAdminUserRole(userId, companyId, role);
        setUsers((current) => replaceUser(current, updated));
        return true;
      } catch (error) {
        showNetworkErrorToast(error);
        return false;
      }
    },
    []
  );

  const setActive = useCallback(async (userId: string, isActive: boolean) => {
    try {
      const updated = await setAdminUserActive(userId, isActive);
      setUsers((current) => replaceUser(current, updated));
      return true;
    } catch (error) {
      showNetworkErrorToast(error);
      return false;
    }
  }, []);

  const assignCompany = useCallback(
    async (userId: string, companyId: string, role?: AssignableRole) => {
      try {
        const updated = await assignAdminUserCompany(userId, companyId, role);
        setUsers((current) => replaceUser(current, updated));
        return true;
      } catch (error) {
        showNetworkErrorToast(error);
        return false;
      }
    },
    []
  );

  const revokeCompany = useCallback(
    async (userId: string, companyId: string) => {
      try {
        const updated = await revokeAdminUserCompany(userId, companyId);
        setUsers((current) =>
          updated === null
            ? current.filter((user) => user.id !== userId)
            : replaceUser(current, updated)
        );
        return true;
      } catch (error) {
        showNetworkErrorToast(error);
        return false;
      }
    },
    []
  );

  return {
    users,
    isLoading,
    isLoadingError,
    reload,
    invite,
    changeRole,
    setActive,
    assignCompany,
    revokeCompany,
  };
};
